<template>
    <div class="d-flex flex-row gap-2">
        <template v-if="!isSaving">
            <GridButton
                @click="addContestant"
                text="gpioreadings.addcontestant"
                :row="row"
                icon="fa-user-plus"
                v-if="!row.UsedFor || showAttachToContestantTypes.includes(row.UsedFor)"
            />
            <GridButton
                @click="attachToGroup"
                text="gpioreadings.attachtogroup"
                :row="row"
                icon="fa-people-group"
                v-if="!row.UsedFor || showAttatchToGroupTypes.includes(row.UsedFor)"
            />
            <GridButton
                @click="attachToCategory"
                text="gpioreadings.attachtocategory"
                :row="row"
                icon="fa-layer-group"
                v-if="!row.UsedFor || showAttachToCategoryTypes.includes(row.UsedFor)"
            />
        </template>
        <span v-else>
            <IconSpinner
                :showSuccess="true"
                :state="LoadState"
            />
        </span>
        <template v-if="row.RaceContestantIDs && row.RaceContestantIDs.length > 0">
            <div
                v-for="(item, i) in row.RaceContestantIDs"
                :key="item"
            >
                <span>
                    {{ GetDisplayName(item) }}
                    <template v-if="i > 0"> ,</template>
                </span>
            </div>
        </template>
        <template v-if="row.GroupIDs && row.GroupIDs.length > 0">
            <div
                v-for="(item, i) in row.GroupIDs"
                :key="item"
            >
                <span>
                    {{ GetGroupName(item) }}
                    <template v-if="i > 0"> ,</template>
                </span>
            </div>
        </template>
        <template v-if="row.CategoryIDs && row.CategoryIDs.length > 0">
            <div
                v-for="(item, i) in row.CategoryIDs"
                :key="item"
            >
                <span>
                    {{ GetCategoryName(item) }}
                    <template v-if="i > 0"> ,</template>
                </span>
            </div>
        </template>
    </div>
</template>
<script setup lang="ts">
import { GridButton, IconSpinner } from '@/components';
import GlobalsearchView from '@/components/Globalsearch/GlobalsearchView.vue';
import type { IGridCompExposed } from '@/components/Grid/Classes';
import { useTaskLoader, useTransactions } from '@/composeables';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import {
    AddTimeRequest,
    AttachGPIOReadingToRequest,
    type ContestantBase,
    type GPIOReading,
    type GPIOReadingUsedFor,
    type TypeAheadItem,
} from '@/objects/generated';
import { useContestantStore, useEventCommonStoreRef, useToastStore } from '@/stores';
import { ref } from 'vue';
import GPIOReadingAttachToDialog from './GPIOReadingAttachToDialog.vue';
import { GPIOReadingDao } from '@/dao';

const props = defineProps<{
    value?: Array<number>;

    row: GPIOReading;

    /**
     * The grid instance. We dont care about the type here
     */
    grid: IGridCompExposed<GPIOReading>;
}>();

const { GetDisplayName } = useContestantStore();

const { GroupsMap, CategoriesMap, Event } = useEventCommonStoreRef();

const GetGroupName = (id: number) => {
    const group = GroupsMap.value.get(id);
    return group ? group.Name : '';
};

const GetCategoryName = (id: number) => {
    const category = CategoriesMap.value.get(id);
    return category ? category.Name : '';
};

const { AddLoader, LoadState } = useTaskLoader();

const isSaving = ref(false);

const showAttatchToGroupTypes: GPIOReadingUsedFor[] = ['None', 'GroupStartTime'];
const showAttachToCategoryTypes: GPIOReadingUsedFor[] = ['None', 'CategoryStartTime'];
const showAttachToContestantTypes: GPIOReadingUsedFor[] = ['None'];

const attachToGroup = async () => {
    const groupIds = ref<number[]>([]);
    const updateContestants = ref(true);

    await useDialogBuilder()
        .withTitle('gpioreadings.attachtogroup')
        .withBodyComponent(GPIOReadingAttachToDialog, {
            componentProps: {
                selectType: 'group',
                reading: props.row,
            },
            namedVModels: {
                groupIds: groupIds,
                updateContestants: updateContestants,
            },
        })
        .withCloseButton({ action: () => {} })
        .withOkButton({
            loadstate: LoadState,
            action: async () => {
                const reading = await GPIOReadingDao.AttachGPIOReadingTo(
                    Event.value!.ID,
                    AttachGPIOReadingToRequest.FromJsValues({
                        GroupIDs: groupIds.value,
                        Reading: props.row,
                        UpdateContestantsStartTime: updateContestants.value,
                    }),
                );
                if (reading) {
                    props.row.UsedFor = reading.UsedFor;
                    props.row.GroupIDs = reading.GroupIDs;
                }
                return true;
            },
        })
        .withOptions({
            Size: 'lg',
            DisableAnimation: true,
        })
        .Open();
};

const attachToCategory = async () => {
    const categoryIds = ref<number[]>([]);
    const updateContestants = ref(true);

    await useDialogBuilder()
        .withTitle('gpioreadings.attachtocategory')
        .withBodyComponent(GPIOReadingAttachToDialog, {
            componentProps: {
                selectType: 'category',
                reading: props.row,
            },
            namedVModels: {
                categoryIds: categoryIds,
                updateContestants: updateContestants,
            },
        })
        .withCloseButton({ action: () => {} })
        .withOkButton({
            loadstate: LoadState,
            action: async () => {
                const reading = await GPIOReadingDao.AttachGPIOReadingTo(
                    Event.value!.ID,
                    AttachGPIOReadingToRequest.FromJsValues({
                        CategoryIDs: categoryIds.value,
                        Reading: props.row,
                        UpdateContestantsStartTime: updateContestants.value,
                    }),
                );
                if (reading) {
                    props.row.UsedFor = reading.UsedFor;
                    props.row.CategoryIDs = reading.CategoryIDs;
                }
                return true;
            },
        })
        .withOptions({
            Size: 'lg',
            DisableAnimation: true,
        })
        .Open();
};

/**
 * Opens the globalsearch dialog to add a contestant to the GPIO reading
 */
const addContestant = async () => {
    const model = ref<TypeAheadItem | null>();
    let closeDialog: (() => Promise<void>) | undefined;

    const dialog = await useDialogBuilder()
        .withTitle('globalsearch.title')
        .withBodyComponent(GlobalsearchView, {
            vmodel: model,
            componentProps: {
                onItemSelected: (item: TypeAheadItem): Promise<void> => handleSelectedSearchItem(item, closeDialog),
            },
        })
        .withCloseButton({ action: () => {} })
        .withOkButton({
            loadstate: LoadState,
            action: () => {
                if (!model.value) {
                    return false;
                }

                handleSelectedSearchItem(model.value);

                return true;
            },
        })
        .withOptions({
            Size: 'md',
            DisableAnimation: true,
        })
        .Open();

    closeDialog = dialog.closeDialog;
};

/**
 * When we receive a selected item from the globalsearch, we want to add it to the GPIO reading and close the dialog
 * @param item
 */
const handleSelectedSearchItem = async (item: TypeAheadItem, closeDialog?: () => Promise<void>): Promise<void> => {
    if (item.DataType != 'Contestant') {
        return;
    }

    const contestant: ContestantBase | undefined = useContestantStore().GetContestantByRaceContestantId(item.ID);
    if (!contestant || contestant.Bib == null) {
        useToastStore().addError('globalsearch.contestantnotfoundornobib');
        return;
    }

    isSaving.value = true;

    await closeDialog?.();

    await AddLoader(async () => {
        // We fire the actual transaction to update the database, we do this after closing the dialog to make it feel more snappy
        try {
            const request = AddTimeRequest.FromJsValues({
                Bib: contestant.Bib,
                Time: props.row.Time,
                StationID: props.row.StationID,
                ContestantID: contestant.ID,
            });

            await useTransactions().AddTIM([request]);
        } catch (e) {
            useToastStore().addError('gpioreadings.erroraddingcontestant');
            console.error(e);
        } finally {
            isSaving.value = false;
        }
    }, '');
};
</script>
